import type { FieldItemSnapshot, FieldStartBlocker } from '@groupe-reca/contracts'

// Repris (adapt) de reca-app-v3 `src/features/markers/domain/fieldMarkerTask.ts` : adresse, navigation, téléphone.

/** Adresse lisible : l'unité n'est jamais retirée ni tronquée. */
export function itemAddress(snapshot: Pick<FieldItemSnapshot, 'addressLine1' | 'addressLine2' | 'formattedAddress'>): string {
  const line1 = snapshot.addressLine1?.trim() ?? ''
  const unit = snapshot.addressLine2?.trim() ?? ''
  if (line1 === '') return snapshot.formattedAddress?.trim() ?? 'Adresse inconnue'
  return unit ? `${line1}, ${unit}` : line1
}

export function hasCoordinates(snapshot: Pick<FieldItemSnapshot, 'latitude' | 'longitude'>): boolean {
  return (
    snapshot.latitude !== null &&
    snapshot.longitude !== null &&
    Number.isFinite(snapshot.latitude) &&
    Number.isFinite(snapshot.longitude)
  )
}

/** Navigation externe (Google Maps) : coordonnées si connues, sinon adresse complète. */
export function navigationUrl(snapshot: FieldItemSnapshot): string {
  const destination = hasCoordinates(snapshot)
    ? `${String(snapshot.latitude)},${String(snapshot.longitude)}`
    : [itemAddress(snapshot), snapshot.city, snapshot.postalCode, 'Québec']
        .filter((part): part is string => Boolean(part?.trim()))
        .join(', ')
  const params = new URLSearchParams({ api: '1', destination, travelmode: 'driving', dir_action: 'navigate' })
  return `https://www.google.com/maps/dir/?${params.toString()}`
}

/** Lien `tel:` normalisé (Amérique du Nord) ; `null` si le numéro n'est pas exploitable. */
export function telHref(phone: string | null | undefined): string | null {
  if (!phone) return null
  const digits = phone.replace(/\D/g, '')
  if (digits.length === 10) return `tel:+1${digits}`
  if (digits.length === 11 && digits.startsWith('1')) return `tel:+${digits}`
  return null
}

export function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '')
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  if (local.length !== 10) return phone
  return `${local.slice(0, 3)} ${local.slice(3, 6)}-${local.slice(6)}`
}

const hoursFormat = new Intl.NumberFormat('fr-CA', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
const dateTimeFormat = new Intl.DateTimeFormat('fr-CA', {
  timeZone: 'America/Toronto',
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
})
const timeFormat = new Intl.DateTimeFormat('fr-CA', { timeZone: 'America/Toronto', hour: '2-digit', minute: '2-digit' })

export function formatEngineHours(value: number): string {
  return `${hoursFormat.format(value)} h`
}

export function formatDateTime(value: string): string {
  return dateTimeFormat.format(new Date(value))
}

export function formatTime(value: string | number): string {
  return timeFormat.format(new Date(value))
}

export function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${String(minutes)} min`
  return `${String(Math.floor(minutes / 60))} h ${String(minutes % 60).padStart(2, '0')}`
}

/** Ce qui bloque le démarrage et ce qu'il faut faire corriger : jamais un choix libre sur la tablette. */
export const blockerMessages: Record<FieldStartBlocker, { title: string; fix: string }> = {
  ROUTE_MISSING: {
    title: 'Aucune route ne vous est assignée.',
    fix: 'Demandez au bureau de vous assigner une route dans Employés.',
  },
  ROUTE_UNAVAILABLE: {
    title: 'Votre route assignée n’est plus utilisable (supprimée ou d’une autre saison).',
    fix: 'Demandez au bureau de corriger votre route dans Employés.',
  },
  ROUTE_EMPTY: {
    title: 'Votre route ne contient aucune résidence admissible.',
    fix: 'Demandez au bureau de vérifier la route dans Routes.',
  },
  ROUTE_IN_USE: {
    title: 'Une autre sortie est déjà en cours sur votre route.',
    fix: 'Une seule mission ouverte par route : vérifiez avec le bureau avant de partir.',
  },
  EQUIPMENT_MISSING: {
    title: 'Aucun tracteur ne vous est assigné.',
    fix: 'Demandez au bureau de vous assigner un tracteur dans Employés.',
  },
  EQUIPMENT_UNAVAILABLE: {
    title: 'Votre tracteur assigné est indisponible (entretien, bris ou retiré).',
    fix: 'Demandez au bureau de vous assigner un autre tracteur dans Employés.',
  },
  EQUIPMENT_IN_USE: {
    title: 'Votre tracteur est déjà engagé dans une autre sortie en cours.',
    fix: 'Vérifiez avec le bureau : la sortie précédente doit être terminée.',
  },
  OPERATOR_IN_OTHER_MISSION: {
    title: 'Une mission ouverte hors de cette application vous est attribuée.',
    fix: 'Demandez au bureau de la fermer avant de démarrer.',
  },
}

const serverMessages: Record<string, string> = {
  MISSION_ENGINE_HOURS_BELOW_LAST:
    'Ce relevé est inférieur au dernier relevé du tracteur. Vérifiez le compteur; s’il a été remplacé ou corrigé, le bureau doit l’enregistrer avant votre départ.',
  MISSION_ENGINE_HOURS_BELOW_START: 'Le relevé de retour ne peut pas être inférieur au relevé de départ.',
  MISSION_ENGINE_HOURS_INVALID: 'Relevé invalide : un nombre positif, au plus deux décimales.',
  MISSION_OPERATOR_ALREADY_OPEN: 'Une mission est déjà ouverte pour vous : reprenez-la.',
  MISSION_ROUTE_IN_USE: blockerMessages.ROUTE_IN_USE.title,
  MISSION_EQUIPMENT_IN_USE: blockerMessages.EQUIPMENT_IN_USE.title,
  MISSION_ROUTE_MISSING: blockerMessages.ROUTE_MISSING.fix,
  MISSION_EQUIPMENT_MISSING: blockerMessages.EQUIPMENT_MISSING.fix,
  MISSION_ROUTE_UNAVAILABLE: blockerMessages.ROUTE_UNAVAILABLE.fix,
  MISSION_EQUIPMENT_UNAVAILABLE: blockerMessages.EQUIPMENT_UNAVAILABLE.fix,
  MISSION_ROUTE_EMPTY: blockerMessages.ROUTE_EMPTY.fix,
  MISSION_PAUSED: 'La mission est en pause : reprenez-la avant de confirmer du travail.',
  MISSION_NOT_IN_PROGRESS: 'La mission n’est plus en cours.',
  MISSION_NOT_OPEN: 'Cette mission est déjà terminée ou annulée.',
  MISSION_ITEM_ALREADY_COMPLETED: 'Cette résidence est déjà terminée.',
  MISSION_PROBLEM_REASON_REQUIRED: 'Choisissez un motif.',
  MISSION_PROBLEM_TEXT_REQUIRED: 'Pour « Autre », expliquez le problème.',
  MISSION_FORBIDDEN: 'Votre compte ne permet plus cette action. Adressez-vous au bureau.',
  MISSION_EMPLOYEE_INACTIVE: 'Votre fiche employé est désactivée. Adressez-vous au bureau.',
  MISSION_OPERATOR_ROLE_REQUIRED: 'Ce compte n’a pas le rôle Opérateur.',
  MISSION_NOT_FOUND: 'Cette mission est introuvable.',
  MISSION_HAS_WORK:
    'Du travail est déjà enregistré sur cette mission : le démarrage ne s’annule plus ici. Terminez la sortie ou appelez le bureau.',
  MISSION_ROUTE_NOT_ASSIGNED: 'Cette route ne vous est plus assignée. La liste de vos routes a été relue.',
  MISSION_PASS_CHANGED:
    'Le bureau a changé le passage de cette route pendant que vous étiez sur cet écran. Vérifiez la route relue, puis démarrez de nouveau.',
  MISSION_ROUTE_COMPLETED_IN_OPERATION:
    'Toutes les résidences de votre route sont déjà faites ou prises en charge dans l’opération en cours.',
  MISSION_TASK_OWNED_BY_OTHER: 'Cette résidence a été prise en charge par une autre mission.',
  MISSION_TASK_RESOLVED: 'Le bureau a déjà réglé cette résidence.',
  TASK_ALREADY_CLAIMED: 'Déjà prise en charge par un autre opérateur.',
  OFFER_REVOKED: 'Le bureau a retiré cette offre.',
  OFFER_NOT_FOUND: 'Cette offre n’existe plus.',
  OFFER_NOT_FOR_OPERATOR: 'Cette offre ne vous est pas destinée.',
  MISSION_NOT_IN_OPERATION: 'Votre mission n’appartient pas à l’opération de cette offre.',
  OPERATION_TASK_VERSION_CONFLICT: 'La résidence vient de changer au bureau : la liste est relue, réessayez.',
  OPERATION_TASK_NOT_UNRESOLVED: 'Cette résidence est déjà réglée.',
  REINFORCEMENT_MISSION_REQUIRED: 'Commencez le renfort avant de prendre une résidence.',
  PRESENCE_NOT_CONNECTED: 'La tablette n’est pas encore connectée au centre : patientez quelques secondes et réessayez.',
  OPERATOR_IN_MISSION: 'Vous avez une mission ouverte : vous n’êtes pas disponible pour aider.',
  OPERATION_NOT_OPEN: 'Aucune opération n’est ouverte : le renfort n’est pas possible.',
  HELP_REQUEST_COMMENT_TOO_LONG: 'Commentaire trop long : 500 caractères au plus.',
}

export function serverMessage(code: string): string {
  return serverMessages[code] ?? 'Le serveur a refusé l’opération. Réessayez ou prévenez le bureau.'
}

export const engineHoursErrors: Record<'EMPTY' | 'NEGATIVE' | 'INVALID' | 'TOO_PRECISE' | 'TOO_LARGE', string> = {
  EMPTY: 'Saisissez le compteur d’heures du tracteur.',
  NEGATIVE: 'Le compteur ne peut pas être négatif.',
  INVALID: 'Saisissez un nombre, par exemple 1520,5.',
  TOO_PRECISE: 'Deux décimales au plus.',
  TOO_LARGE: 'Valeur trop grande pour un compteur d’heures.',
}
